import { api } from "#/lib/api-client";
import type {
  TrainingApprovalRequest,
  TrainingCategory,
  TrainingCategoryCreateRequest,
  TrainingCategoryUpdateRequest,
  TrainingEnrollment,
  TrainingPageResponse,
  TrainingProgramCreateRequest,
  TrainingProgramDetails,
  TrainingProgramPageResponse,
  TrainingProgramUpdateRequest,
  TrainingPublishRequest,
  TrainingRequestCreateRequest,
  TrainingRequestDetails,
  TrainingRequestDocument,
} from "./types";

const BASE = "/api/hr/training/requests";
const PROG_BASE = "/api/hr/training/programs";
const CAT_BASE = "/api/hr/training/categories";

function qs(params: Record<string, unknown>) {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== "") out.set(k, String(v));
  }
  return out.toString();
}

export const trainingApi = {
  // ── Categories ──────────────────────────────────────────────────────────────
  categories(): Promise<TrainingCategory[]> {
    return api
      .get<TrainingCategory[] | { content: TrainingCategory[] }>(CAT_BASE)
      .then((r) => {
        const d = r.data;
        if (Array.isArray(d)) return d;
        if (d && "content" in d && Array.isArray(d.content)) return d.content;
        return [];
      });
  },

  createCategory(payload: TrainingCategoryCreateRequest): Promise<TrainingCategory> {
    return api.post<TrainingCategory>(CAT_BASE, payload).then((r) => r.data);
  },

  updateCategory(id: number, payload: TrainingCategoryUpdateRequest): Promise<TrainingCategory> {
    return api.put<TrainingCategory>(`${CAT_BASE}/${id}`, payload).then((r) => r.data);
  },

  deleteCategory(id: number): Promise<void> {
    return api.delete(`${CAT_BASE}/${id}`).then(() => undefined);
  },

  /** Mandatory trainings — HR-broadcast trainings visible to all employees */
  mandatoryTrainings(params: { page?: number; size?: number } = {}): Promise<TrainingPageResponse> {
    return api
      .get<TrainingPageResponse>(`${BASE}?${qs({ mandatory: true, page: 0, size: 20, ...params })}`)
      .then((r) => r.data);
  },

  // ── Categories (admin — all including inactive) ──────────────────────────────
  categoriesAll(): Promise<TrainingCategory[]> {
    return api
      .get<TrainingCategory[] | { content: TrainingCategory[] }>(`${CAT_BASE}/all`)
      .then((r) => {
        const d = r.data;
        if (Array.isArray(d)) return d;
        if (d && "content" in d && Array.isArray(d.content)) return d.content;
        return [];
      });
  },

  // ── Programs ──────────────────────────────────────────────────────────────────
  programs: {
    list(params: { page?: number; size?: number; status?: string; search?: string } = {}): Promise<TrainingProgramPageResponse> {
      return api.get<TrainingProgramPageResponse>(`${PROG_BASE}?${qs({ page: 0, size: 15, ...params })}`).then(r => r.data);
    },

    get(id: number): Promise<TrainingProgramDetails> {
      return api.get<TrainingProgramDetails>(`${PROG_BASE}/${id}`).then(r => r.data);
    },

    create(payload: TrainingProgramCreateRequest): Promise<TrainingProgramDetails> {
      return api.post<TrainingProgramDetails>(PROG_BASE, payload).then(r => r.data);
    },

    update(id: number, payload: TrainingProgramUpdateRequest): Promise<TrainingProgramDetails> {
      return api.put<TrainingProgramDetails>(`${PROG_BASE}/${id}`, payload).then(r => r.data);
    },

    delete(id: number): Promise<void> {
      return api.delete(`${PROG_BASE}/${id}`).then(() => undefined);
    },

    publish(id: number, payload: TrainingPublishRequest): Promise<TrainingProgramDetails> {
      return api.post<TrainingProgramDetails>(`${PROG_BASE}/${id}/publish`, payload).then(r => r.data);
    },

    cancel(id: number): Promise<TrainingProgramDetails> {
      return api.post<TrainingProgramDetails>(`${PROG_BASE}/${id}/cancel`).then(r => r.data);
    },

    complete(id: number): Promise<TrainingProgramDetails> {
      return api.post<TrainingProgramDetails>(`${PROG_BASE}/${id}/complete`).then(r => r.data);
    },

    enroll(id: number, payload: TrainingPublishRequest): Promise<TrainingProgramDetails> {
      return api.post<TrainingProgramDetails>(`${PROG_BASE}/${id}/enroll`, payload).then(r => r.data);
    },

    enrollments(id: number): Promise<TrainingEnrollment[]> {
      return api.get<TrainingEnrollment[]>(`${PROG_BASE}/${id}/enrollments`).then(r => r.data);
    },

    markAttended(id: number, eid: number): Promise<TrainingEnrollment> {
      return api.patch<TrainingEnrollment>(`${PROG_BASE}/${id}/enrollments/${eid}/attend`).then(r => r.data);
    },

    markAbsent(id: number, eid: number): Promise<TrainingEnrollment> {
      return api.patch<TrainingEnrollment>(`${PROG_BASE}/${id}/enrollments/${eid}/absent`).then(r => r.data);
    },

    cancelEnrollment(id: number, eid: number): Promise<void> {
      return api.delete(`${PROG_BASE}/${id}/enrollments/${eid}`).then(() => undefined);
    },
  },

  // ── Requests ────────────────────────────────────────────────────────────────
  mine(params: { page?: number; size?: number; status?: string } = {}) {
    return api
      .get<TrainingPageResponse>(`${BASE}/mine?${qs({ page: 0, size: 10, ...params })}`)
      .then((r) => r.data);
  },

  inbox(params: { page?: number; size?: number } = {}) {
    return api
      .get<TrainingPageResponse>(`${BASE}/inbox?${qs({ page: 0, size: 10, ...params })}`)
      .then((r) => r.data);
  },

  all(params: { page?: number; size?: number; status?: string; search?: string } = {}) {
    return api
      .get<TrainingPageResponse>(`${BASE}?${qs({ page: 0, size: 10, ...params })}`)
      .then((r) => r.data);
  },

  get(id: number): Promise<TrainingRequestDetails> {
    return api.get<TrainingRequestDetails>(`${BASE}/${id}`).then((r) => r.data);
  },

  create(payload: TrainingRequestCreateRequest): Promise<TrainingRequestDetails> {
    return api.post<TrainingRequestDetails>(BASE, payload).then((r) => r.data);
  },

  update(id: number, payload: Partial<TrainingRequestCreateRequest>): Promise<TrainingRequestDetails> {
    return api.put<TrainingRequestDetails>(`${BASE}/${id}`, payload).then((r) => r.data);
  },

  submit(id: number): Promise<TrainingRequestDetails> {
    return api.post<TrainingRequestDetails>(`${BASE}/${id}/submit`).then((r) => r.data);
  },

  approve(id: number, payload: TrainingApprovalRequest = {}): Promise<TrainingRequestDetails> {
    return api.post<TrainingRequestDetails>(`${BASE}/${id}/approve`, payload).then((r) => r.data);
  },

  reject(id: number, payload: TrainingApprovalRequest = {}): Promise<TrainingRequestDetails> {
    return api.post<TrainingRequestDetails>(`${BASE}/${id}/reject`, payload).then((r) => r.data);
  },

  cancel(id: number, comments?: string): Promise<TrainingRequestDetails> {
    return api.post<TrainingRequestDetails>(`${BASE}/${id}/cancel`, { comments }).then((r) => r.data);
  },

  uploadCertificate(id: number, file: File, documentType = "CERTIFICATE"): Promise<TrainingRequestDocument> {
    const form = new FormData();
    form.append("file", file);
    if (documentType) form.append("documentType", documentType);
    return api
      .post<TrainingRequestDocument>(`${BASE}/${id}/certificate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  deleteDocument(id: number, documentId: number): Promise<void> {
    return api.delete(`${BASE}/${id}/documents/${documentId}`).then(() => undefined);
  },
};
